import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar as CalendarIcon, DoorOpen, Hospital, Building2, Clock, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { getAvailableSlots, urgencyLevels } from "@/data/platformData";
import { MDRBadge } from "@/components/MDRBadge";

const formatDate = (d) => {
  const days = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  return `${days[d.getDay()]} ${d.getDate()}/${d.getMonth() + 1}`;
};

const toISO = (d) => d.toISOString().split("T")[0];

export const BookingSlotStep = ({ booking, updateBooking, eligibleRooms, eligibleStructures }) => {
  // Generate next N days based on urgency
  const urgency = urgencyLevels.find(u => u.id === booking.urgency);
  const maxDays = urgency?.maxDays || 30;
  const visibleDays = Math.min(maxDays, 14);

  const dates = useMemo(() => {
    const out = [];
    const today = new Date();
    for (let i = 0; i < visibleDays; i++) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      out.push(d);
    }
    return out;
  }, [visibleDays]);

  const [selectedDate, setSelectedDate] = useState(booking.date || (dates[1] ? toISO(dates[1]) : null));

  const room = eligibleRooms.find(r => r.id === booking.diagnosticRoomId) || eligibleRooms[0];
  const slots = useMemo(() => {
    if (!room || !selectedDate) return [];
    const totalDuration = booking.serviceIds.reduce((acc, sid) => acc + 5, 15);
    return getAvailableSlots(room.id, selectedDate, totalDuration);
  }, [room, selectedDate, booking.serviceIds]);

  const pickDate = (d) => {
    const iso = toISO(d);
    setSelectedDate(iso);
    updateBooking({ date: iso, time: null });
  };

  const pickTime = (t) => {
    updateBooking({ time: t, date: selectedDate });
  };

  const pickRoom = (rid) => {
    updateBooking({ diagnosticRoomId: rid, time: null });
  };

  const pickStructure = (sid) => {
    updateBooking({ structureId: sid });
  };

  if (eligibleRooms.length === 0) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-semibold">Data e Sala diagnostica</h2>
        <Card className="p-6 border-dashed border-2 text-center bg-amber-50 dark:bg-amber-950/30 border-amber-200">
          <AlertCircle className="h-8 w-8 mx-auto text-amber-600 mb-2" />
          <p className="font-medium text-amber-700">Nessuna sala diagnostica idonea per le prestazioni selezionate</p>
          <p className="text-xs text-amber-700/80 mt-1">Verifica le prestazioni o contatta un'altra farmacia.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold">Data, sala e instradamento</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Verifica disponibilità delle sale diagnostiche e seleziona l'orario.
        </p>
      </div>

      {/* Diagnostic room selector */}
      {eligibleRooms.length > 1 && (
        <div className="space-y-2">
          <p className="text-sm font-medium flex items-center gap-2">
            <DoorOpen className="h-4 w-4 text-primary" /> Sala diagnostica
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {eligibleRooms.map(r => (
              <Card
                key={r.id}
                onClick={() => pickRoom(r.id)}
                data-testid={`booking-room-${r.id}`}
                className={cn(
                  "p-3 cursor-pointer transition-all",
                  room?.id === r.id ? "border-2 border-primary bg-primary/5" : "border hover:border-primary/40"
                )}
              >
                <p className="font-medium text-sm">{r.name}</p>
                <p className="text-xs text-muted-foreground">{r.capabilities.length} prestazioni abilitate</p>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Accredited structure routing (only if requires report) */}
      {eligibleStructures.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium flex items-center gap-2">
              <Hospital className="h-4 w-4 text-rose-600" /> Struttura accreditata per refertazione
            </p>
            <MDRBadge variant="medical" size="sm" />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {eligibleStructures.map(s => (
              <Card
                key={s.id}
                onClick={() => pickStructure(s.id)}
                data-testid={`booking-structure-${s.id}`}
                className={cn(
                  "p-3 cursor-pointer transition-all",
                  booking.structureId === s.id ? "border-2 border-rose-400 bg-rose-50 dark:bg-rose-950/30" : "border hover:border-rose-300"
                )}
              >
                <p className="font-medium text-sm">{s.name}</p>
                <p className="text-xs text-muted-foreground">{s.address}</p>
                <div className="flex flex-wrap gap-1 mt-2">
                  {s.handledServices.slice(0, 4).map(sid => (
                    <Badge key={sid} variant="secondary" className="text-[10px]">{sid}</Badge>
                  ))}
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Date strip */}
      <div className="space-y-2">
        <p className="text-sm font-medium flex items-center gap-2">
          <CalendarIcon className="h-4 w-4 text-primary" /> Data
          <span className="text-xs text-muted-foreground font-normal ml-2">
            ({urgency?.description})
          </span>
        </p>
        <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0">
          <div className="flex gap-2 min-w-max pb-2">
            {dates.map(d => {
              const iso = toISO(d);
              const isSelected = selectedDate === iso;
              const isSunday = d.getDay() === 0;
              return (
                <button
                  key={iso}
                  disabled={isSunday}
                  onClick={() => pickDate(d)}
                  data-testid={`booking-date-${iso}`}
                  className={cn(
                    "flex flex-col items-center justify-center min-w-[72px] px-3 py-3 rounded-lg border-2 transition-all",
                    isSelected ? "border-primary bg-primary text-primary-foreground shadow-md scale-105" : "border-border bg-card hover:border-primary/40",
                    isSunday && "opacity-40 cursor-not-allowed"
                  )}
                >
                  <span className="text-[10px] uppercase font-semibold tracking-wider">{formatDate(d).split(" ")[0]}</span>
                  <span className="text-xl font-bold leading-none mt-1">{d.getDate()}</span>
                  <span className="text-[10px] mt-0.5">{["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"][d.getMonth()]}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Slot grid */}
      <div className="space-y-2">
        <p className="text-sm font-medium flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary" /> Orario disponibile
        </p>
        <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-8 gap-2">
          {slots.length === 0 && (
            <p className="col-span-full text-sm text-muted-foreground text-center py-6">
              Nessuno slot disponibile per questa data
            </p>
          )}
          {slots.map(s => (
            <button
              key={s.time}
              disabled={!s.available}
              onClick={() => pickTime(s.time)}
              data-testid={`booking-slot-${s.time}`}
              className={cn(
                "px-3 py-2.5 rounded-lg border-2 text-sm font-medium transition-all",
                booking.time === s.time && booking.date === selectedDate
                  ? "border-primary bg-primary text-primary-foreground shadow-md"
                  : s.available
                    ? "border-border bg-card hover:border-primary/40"
                    : "border-border bg-muted text-muted-foreground line-through cursor-not-allowed opacity-50"
              )}
            >
              {s.time}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default BookingSlotStep;
